import type { Household } from './household';
import type { Address } from './address';
import type { User } from './user';

export type ResultOrError<TResult, TError> = [null, TResult] | [TError, null];

export interface PhonePrefix {
  prefix: string | null;
  name: string | null;
}

export interface Price {
  complete: number;
  endAssembly: number;
  copper: number;
  fiberOptics: number;
}

export interface Ne4Price {
  private: Price;
  business: Price;
}

export interface ConnectionPrice {
  priceWithProduct: number;
  priceWithoutProduct: number;
  newConstructionArea: string;
  priceMph: number;
  developmentArea: string;
  ne4Price: Ne4Price;
}

export interface BookingApproach {
  isEfhBookingPossible: boolean;
  isMphBookingPossible: boolean;
  isProductBookingPossible: boolean;
  marketingStage: string;
  micromarketId: number;
  micromarketStatus: string;
  addressId: number;
  houseConnectionBooked: boolean;
  ne4Booked: boolean;
}

interface HouseholdCheckResult {
  household?: Household;
  address?: Address;
  customerId: number;
  contractNumber: string;
}

export interface ExistingHousehold {
  result?: HouseholdCheckResult;
  isExisting: boolean;
}

export interface PhoneNumber {
  prefix: string;
  number: string;
}

export interface Contact {
  email: string;
  tel: PhoneNumber;
  mobile: PhoneNumber;
}

export interface Article {
  term: string;
  value: string;
  monthlyPrice: number | null;
  onetimePrice: number | null;
  promoPrice: number | null;
  promoRuntime: number | null;
  articleNumber: string;
  minDurationMonths: string | null;
  disabled: boolean;
  disabledReason: string;
  type: 'product' | 'discount';
  isDiscountApplied: boolean;
}

export interface SelectedArticle {
  name: string;
  article: Article;
}

export interface HouseholdFullData {
  household?: Household;
  address?: Address;
  contact?: Contact;
  differentContactPerson: boolean;
  isAdministrator?: boolean;
}
export interface PresignedUrl {
  url: string;
  fileName: string;
}

export interface CartConfigurationInput {
  selectedProduct?: string;
  selectedLandlineOption?: string;
  selectedPeriod?: string;
  selectedHardware?: string;
  selectedSecurityPackage?: string;
  selectedAbroadOption?: string;
  selectedTVOption?: string;
  invoiceDelivery?: string;
  isUniversalService?: string;
}

export interface OrderResult {
  eeccOrderId?: number;
  activeContractResult?: number;
  passiveContractResult?: number;
}

export interface ActionCode {
  article: string;
  articleNumber: string;
  price: number;
  amount: number;
  description: string;
  discountRelatedToArticleNumber: string;
}
export interface BookingInit {
  currentUser: User;
  bookingApproach: BookingApproach;
  phonePrefixes: PhonePrefix[];
  priceList: ConnectionPrice;
}

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  FATAL = 'fatal',
}

export enum LogPurpose {
  GENERAL = 'general',
  PERFORMANCE = 'performance',
  NETWORK = 'network',
  PROCESS = 'process',
  AUDIT = 'audit',
  SECURITY = 'security',
  UNKNOWN = 'unknown',
}

export interface LogEntry {
  anonymize?: string[];
  context?: { [key: string]: unknown; };
  logLevel?: LogLevel;
  purpose?: LogPurpose;
  message: string;
}
